/**
 * Chunk load recovery after production deploys.
 *
 * Detects chunk load failures via structural signals (no error-message regex) and
 * reloads once so the browser picks up the current bundle.
 *
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

export const CHUNK_RELOAD_STORAGE_KEY = 'blx-chunk-reload-attempted';

export type ChunkLoadRecoveryAction = 'reloading' | 'show-fallback';

const getErrorName = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'name' in error) {
    const name = (error as { name: unknown }).name;
    return typeof name === 'string' ? name : '';
  }
  return '';
};

const getErrorCode = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code: unknown }).code;
    return typeof code === 'string' ? code : '';
  }
  return '';
};

/** Webpack ChunkLoadError or CSS_CHUNK_LOAD_FAILED — no message pattern matching. */
export const isChunkLoadError = (error: unknown): boolean => {
  if (error == null) {
    return false;
  }

  if (getErrorName(error) === 'ChunkLoadError') {
    return true;
  }

  if (getErrorCode(error) === 'CSS_CHUNK_LOAD_FAILED') {
    return true;
  }

  return false;
};

const isSameOriginJsUrl = (url: string): boolean => {
  if (!url || !url.endsWith('.js')) {
    return false;
  }

  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
};

export const isScriptChunkLoadError = (event: Event): boolean => {
  const target = event.target;
  if (!(target instanceof HTMLScriptElement)) {
    return false;
  }

  return isSameOriginJsUrl(target.src);
};

/** HTML served as JS (e.g. SPA fallback) surfaces as a window SyntaxError on the chunk URL. */
export const isWindowChunkScriptError = (event: ErrorEvent): boolean => {
  if (!isSameOriginJsUrl(event.filename)) {
    return false;
  }

  const errorName = getErrorName(event.error);
  return errorName === 'SyntaxError' || errorName === '';
};

const attemptOneTimeReload = (): boolean => {
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, '1');
  } catch {
    return false;
  }

  window.location.reload();
  return true;
};

export const reloadOnceOnChunkError = (error: unknown): boolean => {
  if (!isChunkLoadError(error)) {
    return false;
  }

  return attemptOneTimeReload();
};

export const reloadOnceOnScriptChunkLoadError = (event: Event): boolean => {
  if (!isScriptChunkLoadError(event)) {
    return false;
  }

  return attemptOneTimeReload();
};

export const reloadOnceOnWindowChunkScriptError = (event: ErrorEvent): boolean => {
  if (!isWindowChunkScriptError(event)) {
    return false;
  }

  return attemptOneTimeReload();
};

/** For route/Await error boundaries: reload once on chunk errors, otherwise show fallback UI. */
export const resolveChunkLoadRecovery = (error: unknown): ChunkLoadRecoveryAction | null => {
  if (error == null) {
    return null;
  }

  if (!isChunkLoadError(error)) {
    return 'show-fallback';
  }

  return reloadOnceOnChunkError(error) ? 'reloading' : 'show-fallback';
};

export const clearChunkReloadFlag = (): void => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable in restricted contexts
  }
};

let globalHandlersRegistered = false;

export const registerGlobalChunkLoadRecovery = (): void => {
  if (globalHandlersRegistered || typeof window === 'undefined') {
    return;
  }
  globalHandlersRegistered = true;

  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason == null) {
      return;
    }
    if (reloadOnceOnChunkError(event.reason)) {
      event.preventDefault();
    }
  });

  window.addEventListener(
    'error',
    (event) => {
      if (reloadOnceOnScriptChunkLoadError(event)) {
        event.preventDefault();
        return;
      }

      if (event instanceof ErrorEvent && reloadOnceOnWindowChunkScriptError(event)) {
        event.preventDefault();
      }
    },
    true,
  );
};

export const importWithChunkRetry = <T>(factory: () => Promise<T>): Promise<T> => {
  return factory().catch((error: unknown) => {
    if (reloadOnceOnChunkError(error)) {
      return new Promise<T>(() => ({}));
    }
    throw error;
  });
};

// Mirrors React.lazy — `any` is required so components with specific props infer correctly.
export const lazyWithChunkRetry = <T extends ComponentType<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> => lazy(() => importWithChunkRetry(factory));
